<template>
  <div class="admin-interface">
    <div class="row">
      <div class="col-12">
        <div class="card">
          <div class="card-header">
            <h4 class="card-title">
              <i class="fas fa-users-cog mr-2"></i>
              User Management
            </h4>
            <div class="card-header-actions">
              <b-button variant="success" @click="showCreateUserModal = true">
                <i class="fas fa-plus mr-1"></i>
                Add New User
              </b-button>
            </div>
          </div>
          <div class="card-body">
            <!-- Users Table -->
            <div v-if="$apollo.loading" class="text-center">
              <div class="spinner-border" role="status">
                <span class="sr-only">Loading...</span>
              </div>
            </div>
            <div v-else-if="users && users.length > 0">
              <div class="table-responsive">
                <table class="table table-striped table-hover">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th class="d-none d-sm-table-cell">Email</th>
                      <th class="d-none d-md-table-cell">Phone</th>
                      <th>Role</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="user in users" :key="user.id">
                      <td>{{ user.firstName }} {{ user.lastName }}</td>
                      <td class="d-none d-sm-table-cell">{{ user.email }}</td>
                      <td class="d-none d-md-table-cell">{{ user.phoneNumber || 'N/A' }}</td>
                      <td>
                        <b-badge :variant="user.role === 'ADMIN' ? 'danger' : 'primary'">
                          {{ user.role }}
                        </b-badge>
                      </td>
                      <td class="text-right">
                        <b-dropdown size="sm" variant="outline-secondary" right no-caret>
                          <template #button-content>
                            <i class="fas fa-ellipsis-v"></i>
                          </template>
                          <b-dropdown-item @click="editUser(user)">
                            <i class="fas fa-edit mr-1"></i> Edit
                          </b-dropdown-item>
                          <b-dropdown-item @click="openPasswordModal(user)">
                            <i class="fas fa-key mr-1"></i> Set Password
                          </b-dropdown-item>
                        </b-dropdown>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
            <div v-else class="text-center text-muted">
              <p>No users found.</p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Create User Modal -->
    <b-modal 
      v-model="showCreateUserModal" 
      title="Add New User" 
      size="lg"
      @ok="createUser"
      @cancel="resetCreateForm"
    >
      <form @submit.prevent="createUser">
        <div class="row">
          <div class="col-md-6">
            <b-form-group label="First Name" label-for="create-firstName">
              <b-form-input
                id="create-firstName"
                v-model="createForm.firstName"
                name="create-firstName"
                v-validate="'required'"
                :class="{'is-invalid': errors.has('create-firstName')}"
                required
              />
              <b-form-text class="is-invalid help" v-show="errors.has('create-firstName')">
                {{ errors.first('create-firstName') }}
              </b-form-text>
            </b-form-group>
          </div>
          <div class="col-md-6">
            <b-form-group label="Last Name" label-for="create-lastName">
              <b-form-input
                id="create-lastName"
                v-model="createForm.lastName"
                name="create-lastName"
                v-validate="'required'"
                :class="{'is-invalid': errors.has('create-lastName')}"
                required
              />
              <b-form-text class="is-invalid help" v-show="errors.has('create-lastName')">
                {{ errors.first('create-lastName') }}
              </b-form-text>
            </b-form-group>
          </div>
        </div>
        <div class="row">
          <div class="col-md-6">
            <b-form-group label="Email" label-for="create-email">
              <b-form-input
                id="create-email"
                v-model="createForm.email"
                name="create-email"
                type="email"
                v-validate="'required|email'"
                :class="{'is-invalid': errors.has('create-email')}"
                required
              />
              <b-form-text class="is-invalid help" v-show="errors.has('create-email')">
                {{ errors.first('create-email') }}
              </b-form-text>
            </b-form-group>
          </div>
          <div class="col-md-6">
            <b-form-group label="Phone Number" label-for="create-phone">
              <b-form-input
                id="create-phone"
                v-model="createForm.phoneNumber"
                name="create-phone"
                v-validate="'phoneNumber'"
                :class="{'is-invalid': errors.has('create-phone')}"
              />
              <b-form-text class="is-invalid help" v-show="errors.has('create-phone')">
                {{ errors.first('create-phone') }}
              </b-form-text>
            </b-form-group>
          </div>
        </div>
        <div class="row">
          <div class="col-md-12">
            <b-form-group label="Password" label-for="create-password">
              <b-form-input
                id="create-password"
                v-model="createForm.password"
                name="create-password"
                type="password"
                v-validate="'required|min:8'"
                :class="{'is-invalid': errors.has('create-password')}"
                required
              />
              <b-form-text class="is-invalid help" v-show="errors.has('create-password')">
                {{ errors.first('create-password') }}
              </b-form-text>
            </b-form-group>
          </div>
        </div>
      </form>
    </b-modal>

    <!-- Set Password Modal -->
    <b-modal
      v-model="showPasswordModal"
      title="Set User Password"
      @ok="setUserPassword"
      @cancel="resetPasswordForm"
      @hidden="resetPasswordForm"
    >
      <p v-if="passwordForm.userName" class="text-muted">
        Setting password for <strong>{{ passwordForm.userName }}</strong>
      </p>
      <b-form-group label="New Password" label-for="set-password">
        <b-form-input
          id="set-password"
          v-model="passwordForm.password"
          type="password"
          :state="passwordForm.password.length === 0 ? null : passwordForm.password.length >= 8"
        />
        <b-form-invalid-feedback>Password must be at least 8 characters.</b-form-invalid-feedback>
      </b-form-group>
      <b-form-group label="Confirm Password" label-for="set-password-confirm">
        <b-form-input
          id="set-password-confirm"
          v-model="passwordForm.confirm"
          type="password"
          :state="passwordForm.confirm.length === 0 ? null : passwordForm.confirm === passwordForm.password"
        />
        <b-form-invalid-feedback>Passwords do not match.</b-form-invalid-feedback>
      </b-form-group>
    </b-modal>

    <!-- Edit User Modal -->
    <b-modal 
      v-model="showEditUserModal" 
      title="Edit User" 
      size="lg"
      @ok="updateUser"
      @cancel="resetEditForm"
    >
      <form @submit.prevent="updateUser" v-if="editForm">
        <div class="row">
          <div class="col-md-6">
            <b-form-group label="First Name" label-for="edit-firstName">
              <b-form-input
                id="edit-firstName"
                v-model="editForm.firstName"
                name="edit-firstName"
                v-validate="'required'"
                :class="{'is-invalid': errors.has('edit-firstName')}"
                required
              />
              <b-form-text class="is-invalid help" v-show="errors.has('edit-firstName')">
                {{ errors.first('edit-firstName') }}
              </b-form-text>
            </b-form-group>
          </div>
          <div class="col-md-6">
            <b-form-group label="Last Name" label-for="edit-lastName">
              <b-form-input
                id="edit-lastName"
                v-model="editForm.lastName"
                name="edit-lastName"
                v-validate="'required'"
                :class="{'is-invalid': errors.has('edit-lastName')}"
                required
              />
              <b-form-text class="is-invalid help" v-show="errors.has('edit-lastName')">
                {{ errors.first('edit-lastName') }}
              </b-form-text>
            </b-form-group>
          </div>
        </div>
        <div class="row">
          <div class="col-md-6">
            <b-form-group label="Email" label-for="edit-email">
              <b-form-input
                id="edit-email"
                v-model="editForm.email"
                name="edit-email"
                type="email"
                v-validate="'required|email'"
                :class="{'is-invalid': errors.has('edit-email')}"
                required
              />
              <b-form-text class="is-invalid help" v-show="errors.has('edit-email')">
                {{ errors.first('edit-email') }}
              </b-form-text>
            </b-form-group>
          </div>
          <div class="col-md-6">
            <b-form-group label="Phone Number" label-for="edit-phone">
              <b-form-input
                id="edit-phone"
                v-model="editForm.phoneNumber"
                name="edit-phone"
                v-validate="'phoneNumber'"
                :class="{'is-invalid': errors.has('edit-phone')}"
              />
              <b-form-text class="is-invalid help" v-show="errors.has('edit-phone')">
                {{ errors.first('edit-phone') }}
              </b-form-text>
            </b-form-group>
          </div>
        </div>
        <div class="row">
          <div class="col-md-6">
            <b-form-group label="Role" label-for="edit-role">
              <b-form-select
                id="edit-role"
                v-model="editForm.role"
                :options="roleOptions"
              />
            </b-form-group>
          </div>
        </div>
      </form>
    </b-modal>

  </div>
</template>

<script>
import { Validator } from 'vee-validate'
import PhoneNumber from 'awesome-phonenumber'

import {
  ALL_USERS_QUERY,
  SIGNUP_MUTATION,
  UPDATE_USER_MUTATION,
  UPDATE_USER_ROLE_MUTATION,
  ADMIN_RESET_USER_PASSWORD_MUTATION
} from '@/constants/graphql'

const phoneNumber = {
  getMessage: () => `Not a valid phone number`,
  validate (value) {
    return new Promise(resolve => {
      if (!value) {
        resolve({ valid: true }) // Phone number is optional
        return
      }
      let phone = new PhoneNumber(value, 'US')
      resolve({ valid: phone.isValid() })
    })
  }
}
Validator.extend('phoneNumber', phoneNumber)

export default {
  name: 'AdminInterface',
  data () {
    return {
      showCreateUserModal: false,
      showEditUserModal: false,
      showPasswordModal: false,
      passwordForm: {
        userId: null,
        userName: '',
        password: '',
        confirm: ''
      },
      createForm: {
        firstName: '',
        lastName: '',
        email: '',
        phoneNumber: '',
        password: ''
      },
      editForm: null,
      roleOptions: [
        { value: 'CUSTOMER', text: 'Customer' },
        { value: 'READONLY', text: 'Read Only' },
        { value: 'ADMIN', text: 'Admin' }
      ]
    }
  },
  methods: {
    editUser (user) {
      this.editForm = { ...user }
      this.showEditUserModal = true
    },
    openPasswordModal (user) {
      this.passwordForm.userId = user.id
      this.passwordForm.userName = `${user.firstName} ${user.lastName}`
      this.showPasswordModal = true
    },
    setUserPassword (bvModalEvt) {
      bvModalEvt.preventDefault()
      const { password, confirm, userId } = this.passwordForm
      if (password.length < 8 || password !== confirm) return
      this.$apollo.mutate({
        mutation: ADMIN_RESET_USER_PASSWORD_MUTATION,
        variables: { id: userId, newPassword: password }
      }).then(() => {
        this.$swal.fire({ icon: 'success', title: 'Password updated successfully!' })
        this.showPasswordModal = false
        this.resetPasswordForm()
      }).catch(() => {
        // handled by global onError handler
      })
    },
    resetPasswordForm () {
      this.passwordForm = { userId: null, userName: '', password: '', confirm: '' }
    },
    createUser () {
      this.$validator.validateAll().then((result) => {
        if (result) {
          this.$apollo.mutate({
            mutation: SIGNUP_MUTATION,
            variables: {
              firstName: this.createForm.firstName,
              lastName: this.createForm.lastName,
              email: this.createForm.email,
              password: this.createForm.password
            }
          }).then(() => {
            this.$swal.fire({ 
              icon: 'success', 
              title: 'User created successfully!' 
            })
            this.showCreateUserModal = false
            this.resetCreateForm()
            this.$apollo.queries.users.refetch()
          }).catch(() => {
            // handled by global onError handler
          })
        }
      })
    },
    updateUser () {
      this.$validator.validateAll().then((result) => {
        if (result) {
          const mutations = [
            this.$apollo.mutate({
              mutation: UPDATE_USER_MUTATION,
              variables: {
                id: this.editForm.id,
                data: {
                  firstName: this.editForm.firstName,
                  lastName: this.editForm.lastName,
                  email: this.editForm.email,
                  phoneNumber: this.editForm.phoneNumber
                }
              }
            }),
            this.$apollo.mutate({
              mutation: UPDATE_USER_ROLE_MUTATION,
              variables: {
                id: this.editForm.id,
                role: this.editForm.role
              }
            })
          ]

          Promise.all(mutations).then(() => {
            this.$swal.fire({
              icon: 'success',
              title: 'User updated successfully!'
            })
            this.showEditUserModal = false
            this.resetEditForm()
            this.$apollo.queries.users.refetch()
          }).catch(() => {
            // handled by global onError handler
          })
        }
      })
    },
    resetCreateForm () {
      this.createForm = {
        firstName: '',
        lastName: '',
        email: '',
        phoneNumber: '',
        password: ''
      }
      this.$validator.reset()
    },
    resetEditForm () {
      this.editForm = null
      this.$validator.reset()
    }
  },
  apollo: {
    users: {
      query: ALL_USERS_QUERY
    }
  }
}
</script>

<style scoped>
.admin-interface {
  padding: 20px;
}

.card-header-actions {
  margin-left: auto;
}

.btn-group .btn {
  margin-right: 2px;
}

.btn-group .btn:last-child {
  margin-right: 0;
}

.table th {
  border-top: none;
  font-weight: 600;
}

.spinner-border {
  width: 3rem;
  height: 3rem;
}
</style>
